import { useState, useRef, useEffect, useCallback } from 'react';

const MIN_BRAIN_WIDTH = 280;
const MAX_BRAIN_WIDTH = 560;
const DEFAULT_BRAIN_WIDTH = 340;
const BRAIN_WIDTH_KEY = 'brain-panel-width';

/**
 * Resize the Brain panel from its left gutter (mirror of useSidebarResize).
 * Updates --brain-width on the document during drag for smooth layout.
 */
export function useBrainResize(isOpen) {
  const [brainWidth, setBrainWidth] = useState(() => {
    const saved = localStorage.getItem(BRAIN_WIDTH_KEY);
    const parsed = saved ? parseInt(saved, 10) : DEFAULT_BRAIN_WIDTH;
    return Number.isFinite(parsed) ? parsed : DEFAULT_BRAIN_WIDTH;
  });
  const [isResizing, setIsResizing] = useState(false);
  const panelRef = useRef(null);
  const currentWidthRef = useRef(brainWidth);

  const clampWidth = useCallback((width) => (
    Math.min(MAX_BRAIN_WIDTH, Math.max(MIN_BRAIN_WIDTH, width))
  ), []);

  const applyWidth = useCallback((width) => {
    document.documentElement.style.setProperty('--brain-width', `${width}px`);
    if (panelRef.current) {
      panelRef.current.style.width = `${width}px`;
    }
  }, []);

  const handleResizeStart = useCallback((e) => {
    e.preventDefault();
    setIsResizing(true);
    currentWidthRef.current = brainWidth;
  }, [brainWidth]);

  const handleResizeReset = useCallback(() => {
    currentWidthRef.current = DEFAULT_BRAIN_WIDTH;
    setBrainWidth(DEFAULT_BRAIN_WIDTH);
    localStorage.setItem(BRAIN_WIDTH_KEY, String(DEFAULT_BRAIN_WIDTH));
    applyWidth(DEFAULT_BRAIN_WIDTH);
  }, [applyWidth]);

  useEffect(() => {
    if (!isResizing) return undefined;

    const handleMouseMove = (e) => {
      const fromRight = window.innerWidth - e.clientX;
      const newWidth = clampWidth(fromRight);
      currentWidthRef.current = newWidth;
      applyWidth(newWidth);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      const finalWidth = currentWidthRef.current;
      setBrainWidth(finalWidth);
      localStorage.setItem(BRAIN_WIDTH_KEY, String(finalWidth));
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing, applyWidth, clampWidth]);

  // Keep the CSS var in sync so main/navbar gutters match even before paint
  useEffect(() => {
    document.documentElement.style.setProperty('--brain-width', `${brainWidth}px`);
    if (isOpen && panelRef.current) {
      panelRef.current.style.width = `${brainWidth}px`;
    }
  }, [isOpen, brainWidth]);

  return {
    brainWidth,
    isResizing,
    panelRef,
    handleResizeStart,
    handleResizeReset,
  };
}

export default useBrainResize;
